// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// Hand-authored client-side types for the Investment Reporting IR (Intermediate
// Representation). These mirror docs/investment-reporting/IR-contract-v0.md and the
// shape of the cycle artifacts under docs/investment-reporting/fixtures/.
//
// The load-bearing design here is the NUMBERS / PROSE split, made compiler-enforced:
//   * `Cell`      is the universal numeric unit. It has NO `author` field — and carries
//                 `author?: never` so a Cell can never be mis-typed as prose.
//   * `ProseSlot` is the universal narrative unit. It has NO `value` field — and carries
//                 `value?: never` so a ProseSlot can never smuggle a number in as data.
// A component that renders a Cell can therefore never read `.text`, and a component that
// renders a ProseSlot can never read `.value` — the split the whole estate depends on is
// checked at compile time on the client, not merely by convention.

// ----------------------------------------------------------------------------
// Closed enums (mirror the DDL domains / IR contract). Non-LLM by construction.
// ----------------------------------------------------------------------------

// Cell.status. `ok` = a real, in-scope number. `not_found` = never fabricated, value null,
// rendered as an explicit sentinel. The two quarantine states carry a real value that may
// render ONLY inside a labelled forward-read panel.
export type CellStatus =
  | "ok"
  | "not_found"
  | "quarantined_forward_read"
  | "temporal_quarantine";

// The closed, LLM-FREE provenance engine enum. If a number did not come from one of these,
// it is not a Cell.
export type ProvenanceEngine =
  | "fe_golden_csv"
  | "deterministic_compute"
  | "supabase_pit_reconstruction"
  | "web_verified_market"
  | "vendor_actual"
  | "vendor_chart_verified"
  | "manual_locked";

export type Confidence = "high" | "medium" | "low";

export interface Provenance {
  engine: ProvenanceEngine;
  source_ref: string;
  inputs: string[]; // dotted IR paths this cell was computed from
  computed_at: string;
}

// ----------------------------------------------------------------------------
// Cell — the universal numeric unit. NO `author`.
// ----------------------------------------------------------------------------
export interface Cell {
  value: number | null; // null iff status !== 'ok'
  basis: string; // units + basis string, e.g. "GBP/Q2/TR-bid-bid/%", "pp/Q2"
  status: CellStatus;
  provenance: Provenance;
  confidence: Confidence;
  needs_review: boolean;
  flag_ids: string[];
  note: string | null;

  // Compiler wall: a Cell is NEVER prose. Attempting to author-tag a Cell is a type error.
  author?: never;
  text?: never;
}

// A Cell whose payload is an array of numbers (drift matrices / super-groups / by-level).
export interface CellArray extends Omit<Cell, "value"> {
  value?: never; // array cells carry `values`, never a scalar `value`
  values: (number | null)[];
}

// Either shape shows up in the IR; helpers below narrow at runtime.
export type AnyCell = Cell | CellArray;

// ----------------------------------------------------------------------------
// ProseSlot — the universal narrative unit. NO `value`.
// ----------------------------------------------------------------------------
export interface AdversarialVerify {
  attempted: boolean;
  refuted: boolean;
  note: string;
}

export interface ProseReview {
  needs_review: boolean;
  reviewed_by: string | null;
  adversarial_verify: AdversarialVerify;
  holdings_verified: boolean;
}

export interface ProseSlot {
  text: string;
  author: "llm" | "human";
  cell_refs: string[]; // dotted IR paths — numbers are interpolated from THESE, never parsed from text
  source_refs: string[];
  model?: string;
  review: ProseReview;

  // Compiler wall: a ProseSlot NEVER carries a number as data. Numbers live in the Cells
  // named by cell_refs. Attempting to give a ProseSlot a value/values is a type error.
  value?: never;
  values?: never;
}

// ----------------------------------------------------------------------------
// Flags — the gate/flags source.
// ----------------------------------------------------------------------------
export type FlagSeverity = "info" | "warn";

export interface Flag {
  id: string;
  kind: string; // not_found | needs_review | low_confidence | santander_sum_guard | usd_gbp_basis | temporal_quarantine | ...
  severity: FlagSeverity;
  target: string;
  message: string;
  blocks_gate: boolean;
  resolved: boolean;
  // Resolution provenance. Optional because a producer-minted flag has none, but LOAD-BEARING
  // when present: gate leg (ii-b) four-eyes treats resolved=true with a non-uuid resolved_by
  // as "cleared by nobody identifiable" and refuses the signature. A review surface that reads
  // `resolved` without also reading `resolved_by` therefore reports a jammed gate as cleared.
  // resolve_reporting_flag always writes a uuid here; a direct ir PATCH writes nothing.
  resolved_by?: string | null;
  resolved_at?: string | null;
  // resolve_reporting_flag writes `resolution_note` (0003c:808-815). A flag carrying
  // `resolution` instead is a fingerprint of a direct ir PATCH rather than the audited RPC.
  resolution_note?: string | null;
  resolution?: string | null;
}

// ----------------------------------------------------------------------------
// Sensitivity tiers — the NEW 3-value domain (reporting_sensitivity_tier). The fixture
// still uses the OLD 2-value scheme; SensitivityTierBadge maps old -> new for display.
// ----------------------------------------------------------------------------
export type ReportingTier = "ic_internal" | "adviser_restricted" | "retail_public";
export type LegacyTier = "proprietary" | "client_private";

// ----------------------------------------------------------------------------
// Cycle envelope (mirrors ir.cycle + reporting_cycles row-level fields).
// ----------------------------------------------------------------------------
export interface CyclePeriod {
  type: "quarterly-full" | "monthly-interim";
  quarter?: string | null;
  month?: string | null;
  window_start: string;
  window_end: string;
  as_of_date: string;
  is_webinar_month: boolean;
}

export interface GoldenSource {
  fe_table: string;
  period_label: string;
  basis: string;
  csv_ref: string;
  accepted_by: string | null;
  accepted_at: string | null;
}

export type CycleStatus =
  | "draft"
  | "computed"
  | "verified"
  | "gated"
  | "rendered"
  | "delivered"
  | "failed";
export type CycleStage = "snapshot" | "compute" | "verify" | "gate" | "render" | "deliver";

export interface CycleEnvelope {
  id: string;
  period: CyclePeriod;
  status: CycleStatus;
  current_stage: CycleStage;
  gate_approved: boolean;
  created_at: string;
  created_by: string;
  approved_at: string | null;
  approved_by: string | null;
  golden_source: GoldenSource;
}

// ----------------------------------------------------------------------------
// The IR document. Deliberately permissive on the big data/attribution/market_context
// sub-trees (they are rendered defensively via helpers + guard clauses) but strict on the
// three type-discipline surfaces: Cells, Flags, ProseSlots.
// ----------------------------------------------------------------------------
export interface ReconciliationLevel {
  reconstructed: Cell;
  actual: Cell;
  residual: Cell;
  // `evaluated` is the un-run marker: TRUE only when the level actually reached the
  // ±tolerance comparison. On a not-evaluated branch the producer emits
  // `evaluated:false, pass:null` — an un-run level must never read as a measured pass
  // (that was the partial-evaluation fail-open) NOR as a measured failure.
  // `all_pass` requires every level to be BOTH evaluated AND passing.
  evaluated?: boolean;
  pass: boolean | null;
}

export interface Reconciliation {
  tolerance_pp: number;
  levels: Record<string, ReconciliationLevel>;
  all_pass: boolean;
  p_vs_r_decomposition?: Record<string, unknown>;
}

// A per-P (P1..P5) map of scalar Cells — the shape the Phase-2 engine emits for every
// per-portfolio contribution row (attribution.py build_sector_allocation / _active_contribution /
// _counterfactuals). Keyed "P1".."P5"; NOT a positional CellArray. A member may be a
// not_found Cell (value null) where a leg is absent (e.g. gold P5), never fabricated.
export type PContribution = Record<string, Cell>;

// The P-range Brinson level carries allocation/selection/total only (selection/total surface
// not_found until the structured actual-category-contribution feed is supplied). The R level
// extends it with the snapshot-trap reconciliation fields (actual_gap / pct_explained).
export interface BrinsonPLevel {
  allocation: Cell;
  selection: Cell;
  total: Cell;
}

export interface BrinsonLevel extends BrinsonPLevel {
  actual_gap: Cell;
  pct_explained: Cell;
}

export interface AttributionBlock {
  brinson: {
    R: Record<string, BrinsonLevel>;
    P: Record<string, BrinsonPLevel>;
  };
  sector_allocation: {
    rows: { category: string; q2_return: Cell; contribution: PContribution }[];
    total: { label: string; contribution: PContribution };
  };
  active_contribution: {
    pairs: {
      sleeve: string;
      fund: string;
      q2_diff: Cell;
      contribution: PContribution;
      manager_alpha: PContribution;
    }[];
    manager_alpha_total: PContribution;
    two_truths_note: string;
  };
  counterfactuals: { label: string; held_txt: string; alt_txt: string; contribution: PContribution }[];
  reconciliation: Reconciliation;
}

export interface ScorecardRow {
  cbam_claim: string;
  verdict: "confirm" | "nuance" | "overturn";
  evidence: string;
  confidence: Confidence;
  adversarially_verified: boolean;
  narrative_ref: string;
}

export interface ForwardReadStat {
  value: Cell; // status: quarantined_forward_read — render ONLY in the labelled panel
  label: string;
  sub: string;
}

export interface MarketContextBlock {
  scorecard: {
    counts: { confirm: number; nuance: number; overturn: number };
    rows: ScorecardRow[];
    overturned: ScorecardRow[];
  };
  market_figures: { label: string; q2: Cell }[];
  regime: { message: string; source: string; callouts: { title: string; body: string }[] };
  cohorts: { value: string; label: string; sub: string }[];
  forward_read: { label: string; stats: ForwardReadStat[] };
  scenarios: { scenario: string; prob: string; meaning: string; action: string }[];
  lean_in: { theme: string; value_read: string; runway: string; cbam_action: string; ic_only: boolean }[];
  source: string;
}

// ----------------------------------------------------------------------------
// Benchmarks — `data.benchmarks["1".."5"]`, keyed by RISK LEVEL.
//
// THREE series per level: `cbam` / `afi` / `arc` (M5). There is no fourth.
//
// ⚠️⚠️ `cbam` IS CBAM's INTERNAL BENCHMARK. ⚠️⚠️ Sam (CIO), 2026-08-03: "The
// Internal BM IS the peer composite! The rival houses breakdown is the breakdown of
// that." So `cbam` is the series FE publishes as "CBAM {I..V} Benchmark" and the
// client-facing website labels "Int. BM"; the 9 MPS houses in
// `data.composite_constituents` are its CONSTITUENTS, i.e. the BREAKDOWN of this
// same benchmark — not a competing comparator.
//
// A PRIOR PASS (2026-08-02) modelled these as two series and added `internal`,
// `internal_name`, `internal_basis`, `internal_exceptions` and `vs_internal`. Those
// were duplicates of `cbam` / `vs_cbam` and are REMOVED, not aliased — nothing read
// them (no renderer, no deck spec, no fixture).
//
// `cbam` carries the DERIVED measurement (equal-weight mean of the constituents,
// engine "deterministic_compute"). The FE-PUBLISHED measurement of the SAME quantity
// lands under `cbam_crosscheck` and the two are compared (F9). A divergence stamps
// `needs_review` on both cells and raises a WARN flag; it never silently picks a
// winner and never overwrites `cbam`.
// ----------------------------------------------------------------------------
export type BenchmarkPeriodKey = "q2" | "m6" | "y1";

// A benchmark series: period key -> Cell. Extra period keys (e.g. ARC 3yr/5yr
// bands) are admitted, so this stays a partial record rather than a fixed triple.
export type BenchmarkSeries = Partial<Record<BenchmarkPeriodKey, Cell>> &
  Record<string, Cell | undefined>;

/**
 * F9 — the published-vs-derived cross-check for one period. NOT a comparator: the
 * audit trail proving the two measurements of `cbam` agree.
 * `agrees: null` = NOT EVALUATED (one side absent). An un-run check must never read
 * as a measured agreement — the same discipline as `ReconciliationLevel.pass: null`.
 */
export interface BenchmarkCrosscheckPeriod {
  /** FE-published "CBAM {I..V} Benchmark" value (engine "fe_golden_csv"). */
  published: Cell;
  /** published − derived, in pp. `not_found` when either side is absent. */
  diff: Cell;
  agrees: boolean | null;
}

export interface BenchmarkCrosscheck {
  tolerance_pp: number;
  /** Dotted IR path of the derived side, e.g. "data.benchmarks.3.cbam". */
  derived_ref: string;
  periods: Partial<Record<BenchmarkPeriodKey, BenchmarkCrosscheckPeriod>> &
    Record<string, BenchmarkCrosscheckPeriod | undefined>;
}

export interface BenchmarkLevel {
  /**
   * CBAM's internal benchmark for this risk level ("Int. BM" on the website),
   * carried as the DERIVED equal-weight mean of its 9 constituents
   * (deterministic_compute). Breakdown: `data.composite_constituents`.
   */
  cbam: BenchmarkSeries;
  /**
   * AFI on the CORRECTED ladder (Sam, CIO, 2026-08-03 — "the website is the correct
   * lookup for AFI"): L1 = **no comparator at all** · L2 = AFI Cautious · L3 = AFI
   * Balanced · L4 = 50:50 blend of L3/L5 · L5 = AFI Aggressive. FE supplies exactly
   * three series (Cautious→L2, Balanced→L3, Aggressive→L5); only L4 is a blend.
   *
   * ⚠️ AT LEVEL 1 EVERY CELL HERE IS `not_found` WITH `value: null`. That is a KNOWN
   * STRUCTURAL ABSENCE — level 1 genuinely has only two comparators (BM + ARC) — not a
   * data gap and not a completeness failure. Never `0`, never back-filled from an
   * adjacent level. Read `afi_basis` to tell the cases apart.
   */
  afi: BenchmarkSeries;
  arc: BenchmarkSeries;
  /** Short display label, e.g. "CBAM III". */
  cbam_name: string;
  /** `null` at level 1, where no AFI comparator exists. */
  afi_name: string | null;
  /**
   * How this level's AFI cell came to be:
   * `"fe_sourced"` (L2/L3/L5) | `"computed_50_50_blend"` (L4) |
   * `"no_comparator"` (L1 — the series does not exist).
   * Optional: cycles predating the corrected ladder omit it.
   */
  afi_basis?: "fe_sourced" | "computed_50_50_blend" | "no_comparator";
  arc_name: string;
  /** Long form FE publishes for the SAME series, e.g. "CBAM III Benchmark". */
  cbam_fe_name?: string;
  arc_basis: string;
  cbam_basis?: string;
  /** F9 audit trail. Optional: cycles predating the check omit it. */
  cbam_crosscheck?: BenchmarkCrosscheck;
  // ⚠️ `cbam_exceptions` REMOVED 2026-08-03. Sam (CIO): "R uses the same BMs as P
  // throughout." The R1 → "CBAM R Benchmark" exception was a website/DB-seed DATA
  // ERROR, not a business rule, and encoding it here made `cbam_name` conditional for
  // no reason. The CBAM benchmark IS a pure function of risk level for BOTH books at
  // all five levels — as `supabase/functions/sync-risk-daily-values/index.ts:123-129`
  // has always asserted in production (it was the correct side of that conflict; the
  // R1/R5 "CBAM R Benchmark" labels and the `portfolio_benchmarks` seed are the errors,
  // and they are Sam's site, noted not edited). Removed, not aliased: nothing read it.
  // `cbam_name` now describes every portfolio at this level, unconditionally.
}

/**
 * `data.excesses[portfolio]` — one key per comparator; all deterministic_compute.
 * `vs_cbam` IS the website's "Excess vs Int. BM" column (because `cbam` IS the
 * internal benchmark); `vs_afi` is its "Excess vs. AFI".
 *
 * `vs_cbam` is UNCAVEATED for every portfolio in both books: R uses the same
 * benchmarks as P at every level (Sam, CIO, 2026-08-03), so the prior R1 "CBAM R
 * Benchmark" caveat is gone. The values are unchanged — `R1.vs_cbam` always was the
 * excess vs the level-1 series; only the false caveat around it was removed.
 *
 * ⚠️ `P1.vs_afi` and `R1.vs_afi` are ALWAYS `not_found` (`value: null`): no AFI
 * comparator exists at level 1, so no excess against one can. Never `0`, never
 * computed against another level's AFI.
 */
export interface ExcessRow {
  vs_cbam: BenchmarkSeries;
  vs_afi: BenchmarkSeries;
  vs_arc: BenchmarkSeries;
}

export interface ViewSpec {
  sensitivity_tier: LegacyTier | ReportingTier;
  depth: string;
  includes: string[];
  ic_only: boolean;
  covers_ranges: string[];
  target_slides?: number;
  plain_english?: boolean;
  gated_on?: string;
  active_this_cycle?: boolean;
  cadence?: string;
}

// The full IR. `data`, `narrative` sub-shapes are intentionally loose (Record) — the shell
// reads them through guarded helpers, never assuming a field is present. Where a `data`
// sub-tree HAS a declared shape it is exported above for opt-in narrowing at the read site
// (`data.benchmarks[lvl] as BenchmarkLevel`, `data.excesses[pid] as ExcessRow`); `data`
// itself stays permissive so a cycle missing any optional block still type-checks.
export interface IR {
  schema_version: string;
  cycle: CycleEnvelope;
  data: Record<string, any>;
  attribution: AttributionBlock;
  market_context: MarketContextBlock;
  narrative: Record<string, any>;
  views: Record<string, ViewSpec>;
  flags: Flag[];
  _fixture_note?: string;
}

// ----------------------------------------------------------------------------
// Runtime narrowing helpers (permissive — the RPC returns loose jsonb).
// ----------------------------------------------------------------------------
export function isCell(x: unknown): x is Cell {
  return !!x && typeof x === "object" && "status" in (x as any) && "provenance" in (x as any);
}

export function isCellArray(x: unknown): x is CellArray {
  return isCell(x) && Array.isArray((x as any).values);
}

export function isProseSlot(x: unknown): x is ProseSlot {
  return !!x && typeof x === "object" && "text" in (x as any) && "cell_refs" in (x as any);
}

// Resolve a dotted IR path (e.g. "data.portfolio_returns.P1.q2") against the IR document.
// Used by ProseSlotBlock to interpolate numbers from cell_refs — NEVER from prose text.
export function resolveIrPath(ir: unknown, path: string): unknown {
  if (!ir || !path) return undefined;
  // Honour trailing [index] / [Key] shorthands the fixture uses in prose refs, e.g.
  // "data.fund_returns.pairs[Japan].active_q2" or "key_contributors[0].q2":
  // resolve the base key, THEN index into it — numeric index for arrays, string key
  // for objects. A bare (index-less) segment resolves as before.
  return path.split(".").reduce<any>((acc, rawKey) => {
    if (acc == null) return undefined;
    const match = rawKey.match(/^([^[]*)\[([^\]]*)\]$/);
    if (!match) return acc[rawKey];
    const [, base, rawIndex] = match;
    const container = base ? acc[base] : acc;
    if (container == null) return undefined;
    const numIndex = Number(rawIndex);
    return container[rawIndex === "" || Number.isNaN(numIndex) ? rawIndex : numIndex];
  }, ir as any);
}
